<template>
  <b-row style="border-radius: 20px" class="m-1 box-shadow2 p-4 dashHead">
    <b-overlay :show="loading" :variant="!isDarkMode ? 'light' : 'dark'" rounded="sm" no-wrap>
      <template #overlay>
        <div class="d-flex justify-content-center align-items-center h-100">
          <b-spinner :variant="!isDarkMode ? 'dark' : 'light'" label="Loading"></b-spinner>
        </div>
      </template>
    </b-overlay>
    <b-col>
      <b-row class="mt-1" style="justify-content: space-between">
        <b-col xs="12" sm="12" md="4" lg="3" xl="2">
          <h1 class="display-3 font-weight-bold">{{ dashboardDetails.overallCompletion ?
            dashboardDetails.overallCompletion
            : 0 }}%</h1>
        </b-col>
        <b-col xs="12" sm="12" md="8" lg="9" xl="10">
          <b-row class="align-items-center justify-content-between">
            <!-- Left side: Heading -->
            <b-col>
              <h2>{{ dashboardDetails.heading ? dashboardDetails.heading : '' }}</h2>
            </b-col>

            <!-- Right side: Checkbox + Button -->
            <b-col cols="auto" class="d-flex align-items-center justify-content-end">
              <template v-if="switchRequired">
                <b-form-checkbox v-model="checked" name="check-button" switch class="mb-0 mr-3">
                  Correct
                </b-form-checkbox>
              </template>

              <b-button data-v-step="3" v-if="showAdd" size="sm"
                class="d-flex align-items-center justify-content-center mb-0 mr-2" :class="{
                  'box-shadow2 btn btn-success': !isDarkMode,
                  'btn btn-primary text-light': isDarkMode
                }" @click="clickAdd">
                <span class="spinner spinner-white" v-if="aSMReportLoading"></span>
                <font-awesome-icon v-if="!aSMReportLoading" :icon="['fas', 'download']" class="mr-1" />
                {{ aSMReportLoading ? 'Downloading..' : addText }}
              </b-button>

              <b-button v-if="showRoute" v-b-tooltip.hover title="Go to ASM Dashboard" :class="{
                'box-shadow2 btn btn-success btn-sm ': !isDarkMode,
                'btn btn-primary btn-sm text-light ': isDarkMode
              }" @click="asmDashboard">
                <font-awesome-icon class="mr-1" :icon="['fas', 'arrow-alt-circle-right']" />
              </b-button>
            </b-col>
          </b-row>
          <b-row style="flex-wrap: wrap; row-gap: 30px" class="mt-4">
            <b-col style="font-size: larger" v-for="region in dashboardDetails.regionDetails" :key="region.id">
              {{ trimText(region.name ? region.name : '') }}
              <strong>{{ region.completion ? region.completion : 0 }}%</strong>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <!-- Chart -->
      <b-row v-if="chartVisible" class="mt-3">
        <b-col>
          <span style="font-size: larger">100%</span>
          <b-row style="
              height: 25vh;
              align-items: flex-end;
              border-top: 1px dashed grey;
              flex-wrap: nowrap;
              overflow-x: auto;
              gap: 5px;
            ">
            <b-col v-for="(item, index) in dashboardDetails.score" :key="item.id"
              style="text-align: center; flex: 1; padding: 0">
              <div :class="{
                bar: !isDarkMode,
                'bar bg-secondary': isDarkMode
              }" :style="{
                  height: (item.completion / 100) * 21.5 + 'vh',
                  borderRadius: '5px',
                  backgroundColor: item.completion < 90 ? '#888' : '#E88A3E'
                }"></div>
              <span style="font-size: larger">
                {{ index + 1 }}
              </span>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <b-row class="m-1 bordered">
            <b-col>
              <div class="mb-1" v-for="(item, index) in dashboardDetails.checklists" :key="index">
                <b-card no-body :class="{
                  'accordion-card': !isDarkMode,
                  'accordion-card bg-secondary text-light': isDarkMode
                }">
                  <b-card-header header-tag="header" role="tab" class="accordion-header">
                    <b-button :class="{
                      'accordion-toggle': !isDarkMode,
                      'accordion-toggle bg-secondary text-light': isDarkMode
                    }" v-b-toggle="getCollapseId(item.checklist, item.appendText)" variant="light"
                      class="accordion-toggle">
                      <span class="checklist-title">{{ item.checklist }}</span>
                      <span class="completion-percentage">{{ item.compliance ? item.compliance : 0 }}%</span>
                      <span class="chevron-icon"><font-awesome-icon :icon="['fas', 'chevron-down']" /></span>
                    </b-button>
                  </b-card-header>

                  <b-collapse v-if="item.regions && item.regions.length" :class="{
                    '': !isDarkMode,
                    'bg-dark text-light': isDarkMode
                  }" :id="getCollapseId(item.checklist, item.appendText)" accordion="my-accordion">
                    <b-card style="padding: 0; line-height: 0" :class="{
                      '': !isDarkMode,
                      'bg-dark border text-light': isDarkMode
                    }" v-for="(subItem, subIndex) in item.regions" :key="subIndex">
                      <b-card-body style="padding: 0">
                        <p>{{ subItem.region ? subItem.region : '' }}:
                          {{ subItem.compliance ? subItem.compliance : 0 }}%</p>
                      </b-card-body>
                    </b-card>
                  </b-collapse>
                </b-card>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import { mapState } from 'vuex';
import router from '@/router';
export default {
  props: {
    dashboardDetails: Object,
    loading: Boolean,
    chartVisible: Boolean,
    checked: Boolean,
    switchRequired: Boolean,
    addText: String,
    showAdd: Boolean,
    showRoute: Boolean,
    aSMReportLoading: Boolean
  },
  watch: {
    checked(newVal) {
      this.$emit('update-image-type', newVal ? 'correct' : 'incorrect');
    }
  },
  computed: {
    ...mapState('darkMode', ['isDarkMode']),
    bars() {
      const defaultBars = Array.from({ length: 6 }, (_, i) => ({
        label: (i + 1) * 5
      }));
      return defaultBars.map((bar, index) => ({
        ...bar,
        ...(this.dashboardDetails.chartData[index] || {})
      }));
    }
  },
  methods: {
    asmDashboard() {
      router.push('/asmDashboard');
    },
    getCollapseId(checklist, text) {
      return `${checklist.replace(/\s+/g, '-')}-${text}`; // Replace spaces with hyphens
    },
    trimText(text) {
      return text.length > 10 ? text.substring(0, 15) + '...' : text;
    },
    clickAdd() {
      this.$emit('add', {});
    }
  }
};
</script>

<style scoped>
@media (max-width: 1040px) {
  .dashHead {
    margin-top: 100px;
  }
}

::-webkit-scrollbar {
  width: 4px;
  /* Adjust width as needed */
  height: 4px;
}

/* Track (background) */

::-webkit-scrollbar-track {
  background: #f1f1f1;
  /* Light gray background */
}

/* Handle (the draggable part) */

::-webkit-scrollbar-thumb {
  background: #888;
  /* Darker gray scrollbar */

  border-radius: 5px;
}

/* Handle on hover */

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.bar {
  transition: height 0.3s ease-in-out;
}

.bordered {
  border: 1px solid #f1f1f1;
  border-radius: 10px;
  padding: 10px;
}

/* Main Accordion Card */
.accordion-card {
  border: none;
  box-shadow: none;
  background-color: none;
}

/* Accordion Header */
.accordion-header {
  padding: 0;
}

/* Toggle Button */
.accordion-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  font-size: 16px;
  font-weight: bold;
  background: none !important;
  border: none;
  text-align: left;
  box-shadow: none;
}

/* Checklist Title */
.checklist-title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Completion Percentage */
.completion-percentage {
  font-size: 16px;
  font-weight: bold;
  margin-right: 10px;
}

/* Chevron Icon */
.chevron-icon {
  font-size: 14px;
  transition: transform 0.3s ease-in-out;
}

/* Rotate Chevron When Open */
.b-collapse:not(.show) .chevron-icon {
  transform: rotate(0deg);
}

.b-collapse.show .chevron-icon {
  transform: rotate(180deg);
}
</style>
